import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { PatientsList, PatientsListParams } from '@/types/patient-api';

/**
 * GET /api/Patients/GetPatientList — server-paginated patient list.
 * Search is debounced by the consumer (list page does 400 ms).
 * Filter fields (IsArchived, IsMerged, Status) are omitted from the URL
 * when undefined so the backend default applies.
 */
export function usePatientsList(params: PatientsListParams | null) {
  return useQuery({
    queryKey: ['patients-list', params],
    queryFn: ({ signal }) => {
      if (!params) throw new Error('usePatientsList called without params');
      const qs = new URLSearchParams({
        OrganizationId: params.organizationId,
        PageNumber: String(params.pageNumber),
        PageSize: String(params.pageSize),
      });
      if (params.search) qs.set('Search', params.search);
      if (params.isArchived !== undefined) qs.set('IsArchived', String(params.isArchived));
      if (params.isMerged !== undefined) qs.set('IsMerged', String(params.isMerged));
      if (params.status !== undefined) qs.set('Status', String(params.status));
      return get<PatientsList>(`/api/Patients/GetPatientList?${qs.toString()}`, { signal });
    },
    enabled: !!params?.organizationId,
  });
}
